
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/base/SEOHead';

export default function HomePage() {
  const { t } = useTranslation();
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);

  const tracks = [
    {
      id: 'break-the-ground',
      title: t('home.track_1_title'),
      image:
        'https://static.wixstatic.com/media/4b4a97_cb39ec9793c14dbc817b6fc6abe1dd48~mv2.png',
      duration: '3:45',
      description: t('home.track_1_desc'),
      soundcloudUrl: 'https://soundcloud.com/dj-jean-claude-bastos/break-the-ground',
      embedUrl:
        'https://w.soundcloud.com/player/?url=https%3A//soundcloud.com/dj-jean-claude-bastos/break-the-ground&color=%2362D6DF&auto_play=false&hide_related=false&show_comments=false&show_user=true&show_reposts=false&show_teaser=false&visual=true',
    },
    {
      id: 'running-free',
      title: t('home.track_2_title'),
      image:
        'https://static.wixstatic.com/media/4b4a97_ee198727a29f4408a24d4beabddbdf74~mv2.png',
      duration: '3:22',
      description: t('home.track_2_desc'),
      soundcloudUrl: 'https://soundcloud.com/dj-jean-claude-bastos/running-free',
      embedUrl:
        'https://w.soundcloud.com/player/?url=https%3A//soundcloud.com/dj-jean-claude-bastos/running-free&color=%2362D6DF&auto_play=false&hide_related=false&show_comments=false&show_user=true&show_reposts=false&show_teaser=false&visual=true',
    },
    {
      id: 'when-we-loved',
      title: t('home.track_3_title'),
      image:
        'https://public.readdy.ai/ai/img_res/1af5cfdf6cd69853bb63a53c939a3e66.jpg',
      duration: '4:12',
      description: t('home.track_3_desc'),
      soundcloudUrl:
        'https://soundcloud.com/dj-jean-claude-bastos/dj-jcb-when-we-loved-1',
      embedUrl:
        'https://w.soundcloud.com/player/?url=https%3A//soundcloud.com/dj-jean-claude-bastos/dj-jcb-when-we-loved-1&color=%2362D6DF&auto_play=false&hide_related=false&show_comments=false&show_user=true&show_reposts=false&show_teaser=false&visual=true',
    },
  ];

  const handleTrackClick = (trackId: string) => {
    if (currentlyPlaying === trackId) {
      setCurrentlyPlaying(null);
    } else {
      setCurrentlyPlaying(trackId);
    }
  };

  return (
    <>
      <SEOHead
        title={t('seo.home.title')}
        description={t('seo.home.description')}
      />
      <div className="min-h-screen bg-black text-white">
        <Navigation />

        {/* Hero Section */}
        <section
          className="min-h-screen flex items-center justify-center relative bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.4)), url('https://readdy.ai/api/search-image?query=Massive%20electronic%20music%20festival%20crowd%20dancing%20with%20hands%20in%20the%20air%2C%20spectacular%20light%20show%20with%20colorful%20laser%20beams%2C%20LED%20screens%2C%20fireworks%2C%20and%20stage%20lighting%20effects%2C%20Swiss%20Alps%20mountains%20silhouette%20in%20background%2C%20energetic%20festival%20atmosphere%20at%20night%2C%20no%20DJ%20visible%2C%20focus%20on%20crowd%20energy%20and%20amazing%20light%20display%2C%20vibrant%20neon%20colors%2C%20dynamic%20festival%20lighting%20design&width=1920&height=1080&seq=hero-crowd-lights&orientation=landscape')`,
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white w-full">
            <h1
              className="text-6xl md:text-8xl xl:text-9xl font-black mb-8 leading-tight tracking-wide"
              style={{ fontFamily: '"Orbitron", sans-serif' }}
            >
              <span className="bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500 bg-clip-text text-transparent animate-pulse">
                {t('home.hero_title_1')}
              </span>
              <br />
              <span className="text-white font-extrabold drop-shadow-2xl">
                {t('home.hero_title_2')}
              </span>
            </h1>
            <p
              className="text-2xl md:text-3xl xl:text-4xl mb-10 max-w-4xl mx-auto leading-relaxed font-bold"
              style={{ fontFamily: '"Exo 2", sans-serif' }}
            >
              <span className="text-cyan-300 uppercase tracking-wider">
                {t('home.hero_subtitle')}
              </span>
              <br />
              <span className="text-white/90 font-light">
                {t('home.hero_description')}
              </span>
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <a
                href="/music"
                className="bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-600 hover:to-purple-700 text-white px-12 py-4 rounded-full font-bold text-xl transition-all transform hover:scale-105 cursor-pointer whitespace-nowrap shadow-2xl"
                style={{ fontFamily: '"Rajdhani", sans-serif' }}
              >
                <i className="ri-play-circle-line mr-3 text-2xl"></i>
                {t('home.cta_experience')}
              </a>
              <a
                href="/contact"
                className="border-3 border-white text-white hover-bg-white hover:text-black px-12 py-4 rounded-full font-bold text-xl transition-all transform hover:scale-105 cursor-pointer whitespace-nowrap shadow-2xl"
                style={{ fontFamily: '"Rajdhani", sans-serif' }}
              >
                <i className="ri-user-line mr-3 text-2xl"></i>
                {t('home.cta_connect')}
              </a>
            </div>
          </div>
        </section>

        {/* Featured Tracks Section */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20">
              <h2
                className="text-5xl md:text-6xl font-black text-gray-900 mb-6 tracking-tight"
                style={{ fontFamily: '"Orbitron", sans-serif' }}
              >
                <span className="bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text text-transparent">
                  {t('home.latest_releases')}
                </span>
              </h2>
              <p
                className="text-2xl text-gray-600 max-w-3xl mx-auto font-semibold"
                style={{ fontFamily: '"Exo 2", sans-serif' }}
              >
                {t('home.latest_releases_subtitle')}
              </p>
            </div>

            {/* New Album Announcement */}
            <div className="bg-gradient-to-r from-purple-900 via-black to-red-900 rounded-3xl p-8 mb-16 text-center border-4 border-purple-500">
              <div className="flex items-center justify-center mb-4">
                <span className="bg-gradient-to-r from-red-500 to-purple-600 text-white px-4 py-2 rounded-full text-sm font-bold mr-4">
                  NEW ALBUM
                </span>
                <span className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black px-4 py-2 rounded-full text-sm font-bold">
                  NEW VIDEOS
                </span>
              </div>
              <h3 className="text-4xl md:text-5xl font-black text-white mb-4" style={{ fontFamily: '"Orbitron", sans-serif' }}>
                ECHOES OF THE DEAD
              </h3>
              <p className="text-xl text-gray-300 mb-6 max-w-2xl mx-auto" style={{ fontFamily: '"Exo 2", sans-serif' }}>
                A haunting journey through dark electronic soundscapes featuring 5 supernatural tracks and 2 new music videos
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/music"
                  className="bg-gradient-to-r from-purple-600 to-red-600 hover:from-purple-700 hover:to-red-700 text-white px-8 py-3 rounded-full font-bold transition-all transform hover:scale-105 cursor-pointer whitespace-nowrap"
                  style={{ fontFamily: '"Rajdhani", sans-serif' }}
                >
                  <i className="ri-music-line mr-2"></i>
                  Listen to Album
                </a>
                <a
                  href="/videos"
                  className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white px-8 py-3 rounded-full font-bold transition-all transform hover:scale-105 cursor-pointer whitespace-nowrap"
                  style={{ fontFamily: '"Rajdhani", sans-serif' }}
                >
                  <i className="ri-video-line mr-2"></i>
                  Watch Videos
                </a>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              {tracks.map((track) => (
                <div
                  key={track.id}
                  className="bg-white rounded-2xl shadow-2xl overflow-hidden hover:shadow-3xl transition-all transform hover:scale-105 cursor-pointer border border-gray-100"
                >
                  {currentlyPlaying === track.id ? (
                    <div className="h-72 w-full">
                      <iframe
                        width="100%"
                        height="288"
                        scrolling="no"
                        frameBorder="no"
                        allow="autoplay"
                        src={track.embedUrl}
                        title={track.title}
                        className="rounded-t-2xl"
                      ></iframe>
                    </div>
                  ) : (
                    <div className="relative">
                      <img
                        src={track.image}
                        alt={track.title}
                        className="w-full h-72 object-cover object-center"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent hover:from-black/80 transition-all flex items-center justify-center opacity-0 hover:opacity-100">
                        <button
                          onClick={() => handleTrackClick(track.id)}
                          className="bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-600 hover:to-purple-700 text-white w-20 h-20 rounded-full flex items-center justify-center transition-all transform hover:scale-110 cursor-pointer shadow-2xl"
                        >
                          <i className="ri-play-fill text-3xl"></i>
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="p-8">
                    <h3
                      className="text-2xl font-black text-gray-900 mb-3 tracking-wide"
                      style={{ fontFamily: '"Rajdhani", sans-serif' }}
                    >
                      {track.title.toUpperCase()}
                    </h3>
                    <p
                      className="text-gray-600 mb-6 font-bold text-sm tracking-wider"
                      style={{ fontFamily: '"Exo 2", sans-serif' }}
                    >
                      {track.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <span
                        className="text-lg font-bold text-purple-600"
                        style={{ fontFamily: '"Orbitron", sans-serif' }}
                      >
                        {track.duration}
                      </span>
                      <div className="flex space-x-3">
                        <button
                          onClick={() => handleTrackClick(track.id)}
                          className={`${
                            currentlyPlaying === track.id
                              ? 'text-cyan-500'
                              : 'text-gray-400'
                          } hover:text-cyan-500 transition-colors cursor-pointer transform hover:scale-125`}
                        >
                          <i
                            className={`text-2xl ${
                              currentlyPlaying === track.id
                                ? 'ri-stop-circle-fill'
                                : 'ri-play-circle-line'
                            }`}
                          ></i>
                        </button>
                        <a
                          href="https://open.spotify.com/artist/7MWynFxiCsINwkfZHunLEU"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-400 hover:text-cyan-500 transition-colors cursor-pointer transform hover:scale-125"
                        >
                          <i className="ri-spotify-line text-2xl"></i>
                        </a>
                        <a
                          href={track.soundcloudUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-400 hover:text-cyan-500 transition-colors cursor-pointer transform hover:scale-125"
                        >
                          <i className="ri-soundcloud-line text-2xl"></i>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* About Preview Section */}
        <section className="py-24 bg-gradient-to-br from-gray-900 via-black to-purple-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2
                  className="text-5xl md:text-6xl font-black text-white mb-8 leading-tight"
                  style={{ fontFamily: '"Orbitron", sans-serif' }}
                >
                  <span className="bg-gradient-to-r from-cyan-400 to-purple-500 bg-clip-text text-transparent">
                    {t('home.about_title_1')}
                  </span>
                  <br />
                  <span className="text-white">{t('home.about_title_2')}</span>
                </h2>
                <p
                  className="text-xl text-gray-300 mb-8 leading-relaxed font-semibold"
                  style={{ fontFamily: '"Exo 2", sans-serif' }}
                >
                  {t('home.about_text_1')}
                </p>
                <p
                  className="text-xl text-gray-300 mb-10 leading-relaxed font-semibold"
                  style={{ fontFamily: '"Exo 2", sans-serif' }}
                >
                  {t('home.about_text_2')}
                </p>
                <a
                  href="/about"
                  className="bg-gradient-to-r from-purple-600 to-cyan-500 hover:from-purple-700 hover:to-cyan-600 text-white px-10 py-4 rounded-full font-black text-xl transition-all transform hover:scale-105 cursor-pointer whitespace-nowrap shadow-2xl"
                  style={{ fontFamily: '"Rajdhani", sans-serif' }}
                >
                  {t('home.cta_discover')}
                </a>
              </div>
              <div className="relative">
                <img
                  src="https://static.wixstatic.com/media/4b4a97_435e8ae2f3134cb7bf1f172838922b39~mv2.jpg"
                  alt="Jean-Claude Bastos performing live"
                  className="w-full h-[500px] object-cover object-top rounded-2xl shadow-2xl border-4 border-cyan-400"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-purple-600/20 to-transparent rounded-2xl"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Quote Section */}
        <section className="py-24 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="bg-gradient-to-br from-purple-900 via-black to-cyan-900 text-white rounded-3xl p-16 shadow-2xl border-4 border-cyan-400">
              <i className="ri-double-quotes-l text-6xl text-cyan-400 mb-8 block"></i>
              <blockquote
                className="text-3xl md:text-4xl font-bold mb-8 leading-relaxed"
                style={{ fontFamily: '"Exo 2", sans-serif' }}
              >
                "{t('home.quote')}"
              </blockquote>
              <cite
                className="text-cyan-300 font-black text-2xl tracking-wider"
                style={{ fontFamily: '"Orbitron", sans-serif' }}
              >
                {t('home.quote_author')}
              </cite>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="py-24 bg-gradient-to-r from-cyan-500 via-purple-600 to-pink-500">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2
              className="text-5xl md:text-7xl font-black text-white mb-6 tracking-tight"
              style={{ fontFamily: '"Orbitron", sans-serif' }}
            >
              {t('home.final_cta_title')}
            </h2>
            <p
              className="text-2xl md:text-3xl text-white/90 mb-12 max-w-3xl mx-auto font-bold"
              style={{ fontFamily: '"Exo 2", sans-serif' }}
            >
              {t('home.final_cta_subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <a
                href="/music"
                className="bg-white text-purple-600 hover:bg-gray-100 px-12 py-5 rounded-full font-black text-xl transition-all transform hover:scale-105 cursor-pointer whitespace-nowrap shadow-2xl"
                style={{ fontFamily: '"Rajdhani", sans-serif' }}
              >
                <i className="ri-headphone-line mr-3 text-2xl"></i>
                {t('home.cta_listen')}
              </a>
              <a
                href="https://www.jeanclaudebastosmusic.com"
                target="_blank"
                rel="noopener noreferrer"
                className="border-4 border-white text-white hover:bg-white hover:text-purple-600 px-12 py-5 rounded-full font-black text-xl transition-all transform hover:scale-105 cursor-pointer whitespace-nowrap shadow-2xl"
                style={{ fontFamily: '"Rajdhani", sans-serif' }}
              >
                <i className="ri-external-link-line mr-3 text-2xl"></i>
                {t('home.cta_website')}
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
